import { z } from 'zod';
import { digestSchema } from '../../hashing/hash.js';
import { isWithinPage, mediaBoxSchema, pdfRectSchema, pdfRotationSchema } from './geometry.js';
/**
 * A PDF template: the original state form, plus where its fields sit.
 *
 * PLAN §5 makes this a first-class document and §6 requires a state-form revision to be a new
 * template version with zero code. The candidate must see the real I-9, the Virginia sworn
 * disclosure and the background authorization — the actual documents, not HTML that
 * approximates them, because what gets filed has to be recognisable to whoever receives it.
 *
 * Versioned like a step definition and for the same reason: the pinned version is the only
 * input to an artifact, forever. Regenerating a signed form against a later template would
 * change what a person is recorded as having signed.
 */
export const pdfFieldKindSchema = z.enum(['text', 'checkbox', 'date', 'typed-signature']);
/**
 * Where a field's printed value comes from (ADR-014 §1).
 *
 * `answer` is the person's own input, the default and the only source before ADR-014. Every
 * other value is SERVER-FILLED: resolved at fill time from what the agency already holds, shown
 * to the person so they can see what will be printed, and never accepted from the portal.
 *
 * CLOSED on purpose. Extending it is a code change, not configuration: a template must not be
 * able to name an arbitrary property path and have the server print whatever lives there onto
 * a state form. `person.ssn` and `person.dateOfBirth` are named here so a template can map
 * them; resolving their values is #217 (after the ADR-012 sensitive store, #201).
 */
export const pdfFieldSourceSchema = z.enum([
    'answer',
    'person.legalName',
    'person.address',
    'person.ssn',
    'person.dateOfBirth',
    'server.signedDate',
]);
/**
 * Server-filled sources whose value a submission never records (ADR-012, ADR-014 §1).
 *
 * The submission's `printed` map holds `'on-file'` for these, and the filed PDF's sha256 is
 * the proof of what was printed. Every other server-filled value is recorded as printed.
 */
export const SENSITIVE_PDF_FIELD_SOURCES = new Set(['person.ssn', 'person.dateOfBirth']);
/** What `printed` holds for a sensitive server-filled field, in place of its value. */
export const PRINTED_ON_FILE = 'on-file';
export const pdfFieldSchema = z.object({
    id: z.string().min(1),
    /**
     * The human name: the portal's accessible label and "still needed" list, the mapper, and
     * the Answers so far tab. Never part of the fill. Null on versions published before
     * ADR-014; read it through `pdfFieldLabel`.
     */
    label: z.string().trim().min(1).nullable().default(null),
    /** Defaulted, so every version stored before ADR-014 parses as all-answer fields. */
    source: pdfFieldSourceSchema.default('answer'),
    /** Zero-based, and checked against the version's own pageCount at publish. */
    page: z.number().int().nonnegative(),
    kind: pdfFieldKindSchema,
    /**
     * PDF POINTS in absolute user space — the page's own coordinates, not offsets from its
     * MediaBox corner. Never pixels, never viewport-relative. See `pdfRectSchema`.
     */
    rect: pdfRectSchema,
    fontSize: z.number().positive().max(72).default(10),
    maxLength: z.number().int().positive().nullable().default(null),
    required: z.boolean().default(false),
    /**
     * The AcroForm field to set, when the source PDF has a form layer.
     *
     * Null for the scanned state forms that have none — those are filled by drawing text at the
     * mapped rect instead. Both paths use the same rect and the same transform, which is what
     * keeps a form-layer field and a drawn one landing in the same place.
     */
    acroFieldName: z.string().min(1).nullable().default(null),
});
/** Is this field printed by the server rather than answered by the person? */
export function isServerFilledPdfField(field) {
    return field.source !== 'answer';
}
/** The name shown to a person for this field: its label, or its id made readable. */
export function pdfFieldLabel(field) {
    return field.label ?? field.id.replace(/[-_]/g, ' ');
}
/**
 * Which runtime type is an answer to a field of this kind.
 *
 * One function, in packages/shared, called from both sides — the same rule as
 * `toDeviceRect` and for the same reason. The server decides whether a required field was
 * answered and the portal decides whether the submit button turns on; when those two
 * computed it independently the browser's copy was merely wrong in the same way, so nothing
 * caught it.
 *
 * A checkbox's answer is a BOOLEAN and nothing else. Every other kind's answer is a STRING —
 * a date arrives as text, and a typed signature is the name the person typed.
 */
export function pdfAnswerType(kind) {
    return kind === 'checkbox' ? 'boolean' : 'string';
}
/**
 * Has this field been answered, in the form its kind actually accepts?
 *
 * `kind` is what decides. Asking only "is there a value of some usable type here" is what
 * let a boolean sent for a required text field satisfy the required-answer gate and then
 * file a sworn disclosure with the legally required name blank (#117): the gate saw a
 * boolean and passed, the fill looked for text, found none, and drew nothing.
 *
 * A false checkbox counts as answered. Saying no IS answering, and requiring a tick would
 * turn every required checkbox into a compulsory agreement.
 */
export function isPdfFieldAnswered(field, value) {
    if (pdfAnswerType(field.kind) === 'boolean')
        return typeof value === 'boolean';
    return typeof value === 'string' && value.trim() !== '';
}
/**
 * The keys of a values map that are not field ids on this template version, in order.
 *
 * Answers and the template must match exactly (#125). A key the fill has no field for never
 * reaches the flattened PDF but would still be stored, so the record would say the person
 * answered something the filed document does not show. Called with the PINNED version —
 * a key a later revision added is as unknown to an earlier instance as one nobody defined.
 */
export function undefinedPdfFieldIds(version, values) {
    const ids = new Set(version.fields.map((field) => field.id));
    return Object.keys(values).filter((key) => !ids.has(key));
}
/**
 * The keys of a values map that name a SERVER-FILLED field on this version, in order.
 *
 * #125's exact-match rule carried forward (ADR-014 §1): the answers must match the pinned
 * version's ANSWER fields exactly. A key naming a server-filled field is a real field id, so
 * `undefinedPdfFieldIds` lets it through, but the portal must never supply what the server
 * prints — otherwise a candidate could put any name or date they liked on a sworn disclosure
 * and the record would call it the agency's. Refused by name, answered or not.
 */
export function serverFilledPdfFieldIds(version, values) {
    const server = new Set(version.fields.filter(isServerFilledPdfField).map((field) => field.id));
    return Object.keys(values).filter((key) => server.has(key));
}
/**
 * Everything wrong with a submission's `printed` map against the pinned version.
 *
 * `printed` records what the SERVER put on the document (ADR-014 §1): exactly the version's
 * server-filled fields, each with the value as filled, except the sensitive sources, which
 * hold only `'on-file'` so no SSN or date of birth ever sits in a submission (ADR-012).
 */
export function checkPrintedValues(version, printed) {
    const reasons = [];
    const byId = new Map(version.fields.map((field) => [field.id, field]));
    for (const [id, value] of Object.entries(printed)) {
        const field = byId.get(id);
        if (field === undefined || !isServerFilledPdfField(field)) {
            reasons.push(`"${id}" is not a server-filled field on this form`);
        }
        else if (SENSITIVE_PDF_FIELD_SOURCES.has(field.source) && value !== PRINTED_ON_FILE) {
            reasons.push(`"${id}" may only be recorded as "${PRINTED_ON_FILE}"`);
        }
    }
    return reasons;
}
export const pdfPageSchema = z.object({
    mediaBox: mediaBoxSchema,
    /** Absent on most pages; present and load-bearing on scanned forms. */
    rotation: pdfRotationSchema.default(0),
});
export const pdfTemplateSchema = z.object({
    id: z.string().min(1),
    docType: z.literal('pdf-template').default('pdf-template'),
    label: z.string().min(1),
    currentVersion: z.number().int().positive(),
    retiredAt: z.string().datetime().nullable().default(null),
});
export const pdfTemplateVersionSchema = z.object({
    templateId: z.string().min(1),
    version: z.number().int().positive(),
    docType: z.literal('pdf-template-version').default('pdf-template-version'),
    /** Where the source PDF lives. Bytes never travel in this document. */
    container: z.string().min(1),
    blobName: z.string().min(1),
    /** Of the source bytes, so a stored template can be proved to be the one that was reviewed. */
    sourceHash: digestSchema,
    /**
     * Set on versions committed to protected storage (#188, ADR-009 §2, ADR-014 §1): the records
     * account, `templates` container, read by this immutable `versionId`. Versions published before
     * ADR-009 have none of the three and stay readable where they are, in `container`/`blobName`.
     */
    store: z.literal('records').optional(),
    storedFileId: z.string().uuid().optional(),
    versionId: z.string().min(1).optional(),
    pageCount: z.number().int().positive(),
    /** One entry per page, in order. Length is checked against pageCount at publish. */
    pages: z.array(pdfPageSchema).min(1),
    fields: z.array(pdfFieldSchema),
    provenance: z.object({
        fileName: z.string().min(1), sourceUrl: z.string().url(), sourceItemId: z.string().min(1),
        sourceVersion: z.string().min(1), verifiedAt: z.string().datetime(),
        /** Retained source bytes before an optional appended acknowledgment page. */
        originalSourceHash: digestSchema.optional(),
    }).optional(),
    publishedAt: z.string().datetime(),
    publishedBy: z.string().min(1),
});
/**
 * Is a document filled from this template version SSN-BEARING (ADR-012 §4, #206)?
 *
 * True when the version maps any field to a sensitive server-filled source. Today that is the
 * Virginia sworn disclosure, which prints the full number the server decrypts at fill time —
 * the one place in the system where a Social Security number is written out.
 *
 * DERIVED, and deliberately never a flag on the FileRecord. A stored boolean is a second copy
 * of a fact, and a second copy can be wrong: written by a deploy that predates the field, left
 * behind by a template corrected to stop printing the number, or edited by hand in Data
 * Explorer. The PINNED version is the only thing that cannot drift from what the flattened PDF
 * actually shows, because it is what filled it. `storedFileSchema` is `.strict()`, so a record
 * carrying such a flag does not even parse.
 *
 * Takes the fields alone, so a caller cannot accidentally pass a template's CURRENT version:
 * the answer must come from what the instance pinned.
 */
export function isSensitiveArtifact(version) {
    return version.fields.some((field) => SENSITIVE_PDF_FIELD_SOURCES.has(field.source));
}
/**
 * The template version a pdf-form step instance PINNED, read out of its snapshotted config.
 *
 * `StepInstance.config` is `unknown` — every step type has its own shape — so reaching the
 * pinned version means parsing, not casting. Deliberately NOT strict: the config also carries a
 * heading, bindings and a target, and this reads only the two keys that name the version. Null
 * for every step type that pins no template at all, which is most of them.
 */
export const pinnedPdfTemplateRefSchema = z.object({
    templateId: z.string().min(1),
    templateVersion: z.number().int().positive(),
});
export function pinnedPdfTemplateRef(config) {
    const parsed = pinnedPdfTemplateRefSchema.safeParse(config);
    return parsed.success ? parsed.data : null;
}
/**
 * Everything that must hold before a template version may be published.
 *
 * Checked HERE rather than at fill time. A field mapped onto a page that does not exist, or
 * off the edge of one that does, produces a PDF that looks fine until somebody reads the part
 * that is missing — and by then it has been signed, filed, and is evidence of something
 * nobody intended.
 */
export function checkTemplateVersion(version) {
    const reasons = [];
    if (version.pages.length !== version.pageCount) {
        reasons.push(`pageCount is ${version.pageCount} but ${version.pages.length} page(s) were described`);
    }
    const seen = new Set();
    for (const field of version.fields) {
        if (seen.has(field.id)) {
            // Two fields sharing an id means one silently overwrites the other at fill time.
            reasons.push(`duplicate field id "${field.id}"`);
        }
        seen.add(field.id);
        const page = version.pages[field.page];
        if (page === undefined) {
            reasons.push(`field "${field.id}" is mapped to page ${field.page}, which this template does not have`);
            continue;
        }
        if (!isWithinPage(field.rect, page.mediaBox)) {
            reasons.push(`field "${field.id}" falls outside page ${field.page}`);
        }
        // A server-filled value is text. On a checkbox it would tick or untick a box the person
        // never touched; on a typed signature it would sign in their name.
        if (isServerFilledPdfField(field) && (field.kind === 'checkbox' || field.kind === 'typed-signature')) {
            reasons.push(`field "${field.id}" is a ${field.kind} and cannot be filled by the server`);
        }
    }
    return reasons.length === 0 ? { ok: true } : { ok: false, reasons };
}
//# sourceMappingURL=template.js.map